import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, ReplaySubject, of } from "rxjs";
import { catchError, map, shareReplay, tap } from "rxjs/operators";

import { environment } from "../../environments/environment";

export type RoleKey = "superadmin" | "admin" | "manager" | "worker" | "user";

export type PermissionKey =
  | "users.manage"
  | "schedule.view"
  | "schedule.create"
  | "schedule.generate"
  | "schedule.publish"
  | "schedule.edit"
  | "schedule.delete"
  | "schedule.print"
  | "schedule.manage_scores"
  | "schedule.manage_staff"
  | "schedule.edit_game_type"
  | "seasons.manage"
  | "divisions.manage"
  | "teams.manage"
  | "gyms.manage"
  | "tournaments.manage"
  | "tournaments.divisions.manage"
  | "tournaments.teams.manage"
  | "tournaments.schedule.manage"
  | "tournaments.playoff.manage"
  | "tournaments.gyms.manage"
  | "payments.manage"
  | "payments.repair_data"
  | "payroll.manage"
  | "social.manage"
  | "branding.manage"
  | "settings.manage"
  | "audit_logs.view";

export interface PermissionDefinition {
  key: PermissionKey;
  label: string;
  description: string;
  section?: string;
}

export interface RolePermissionsResponse {
  roles: RoleKey[];
  permissions: PermissionDefinition[];
  rolePermissions: Record<RoleKey, PermissionKey[]>;
  userPermissions: PermissionKey[];
  message?: string;
}

const API_URL = `${environment.apiUrl}/role-permissions`;

export const ROLE_LABELS: Record<RoleKey, string> = {
  superadmin: "Superadmin",
  admin: "Admin",
  manager: "Manager",
  worker: "Worker",
  user: "User",
};

export const DEFAULT_ROLES: RoleKey[] = [
  "superadmin",
  "admin",
  "manager",
  "worker",
  "user",
];

export const DEFAULT_PERMISSION_DEFINITIONS: PermissionDefinition[] = [
  {
    key: "users.manage",
    label: "Users",
    description: "Open the Users page and change account roles.",
    section: "Users",
  },
  {
    key: "schedule.view",
    label: "View Schedule",
    description: "Open the Schedule page and view scheduled games.",
    section: "Schedule",
  },
  {
    key: "schedule.create",
    label: "Create Game",
    description: "Create or import scheduled games.",
    section: "Schedule",
  },
  {
    key: "schedule.generate",
    label: "Generate Schedule Drafts",
    description: "Create, edit, validate, and compare scheduler drafts.",
    section: "Schedule",
  },
  {
    key: "schedule.publish",
    label: "Publish Schedule Drafts",
    description: "Publish scheduler drafts and roll back publications.",
    section: "Schedule",
  },
  {
    key: "schedule.edit",
    label: "Edit Game",
    description: "Edit scheduled game details and notes.",
    section: "Schedule",
  },
  {
    key: "schedule.delete",
    label: "Delete Game",
    description: "Delete scheduled games.",
    section: "Schedule",
  },
  {
    key: "schedule.print",
    label: "Print Schedule",
    description: "Print schedules and game score sheets.",
    section: "Schedule",
  },
  {
    key: "schedule.manage_scores",
    label: "Manage Scores",
    description: "Use scorekeeper/statkeeper tools and edit game statistics.",
    section: "Schedule",
  },
  {
    key: "schedule.manage_staff",
    label: "Manage Staff",
    description: "Assign scorekeepers, referees, and stats staff to games.",
    section: "Schedule",
  },
  {
    key: "schedule.edit_game_type",
    label: "Edit Game Type",
    description: "Change regular season, playoff, or exhibition game type.",
    section: "Schedule",
  },
  {
    key: "seasons.manage",
    label: "Seasons",
    description: "Open and manage seasons, fees, and registration dates.",
    section: "League Setup",
  },
  {
    key: "divisions.manage",
    label: "Divisions",
    description: "Open and manage divisions.",
    section: "League Setup",
  },
  {
    key: "teams.manage",
    label: "Teams",
    description: "Open and manage teams and team rosters.",
    section: "League Setup",
  },
  {
    key: "gyms.manage",
    label: "Gym Locations",
    description: "Open and manage gym locations, courts, and addresses.",
    section: "League Setup",
  },
  {
    key: "tournaments.manage",
    label: "Tournaments",
    description: "Open and manage tournament names, dates, fees, and setup.",
    section: "Tournament",
  },
  {
    key: "tournaments.divisions.manage",
    label: "Divisions",
    description: "Open and manage tournament divisions.",
    section: "Tournament",
  },
  {
    key: "tournaments.teams.manage",
    label: "Teams",
    description: "Open and manage tournament teams.",
    section: "Tournament",
  },
  {
    key: "tournaments.schedule.manage",
    label: "Schedule",
    description: "Open and manage tournament games.",
    section: "Tournament",
  },
  {
    key: "tournaments.playoff.manage",
    label: "Playoff",
    description: "Open and manage tournament playoff brackets.",
    section: "Tournament",
  },
  {
    key: "tournaments.gyms.manage",
    label: "Gym Locations",
    description: "Open and manage tournament gyms and courts.",
    section: "Tournament",
  },
  {
    key: "payments.manage",
    label: "Payments",
    description: "Open payment tracking and manage admin payments.",
    section: "Finance",
  },
  {
    key: "payments.repair_data",
    label: "Repair Payment Data",
    description: "Edit payment team links and run payment backfills.",
    section: "Finance",
  },
  {
    key: "payroll.manage",
    label: "Payroll",
    description: "Open payroll, staff rates, time logs, and contracts.",
    section: "Finance",
  },
  {
    key: "social.manage",
    label: "Social Media",
    description: "Generate and download social media graphics.",
    section: "Marketing",
  },
  {
    key: "branding.manage",
    label: "League Profile",
    description: "Update league branding and contact details, and publish public Rules and Refund policies.",
    section: "Settings",
  },
  {
    key: "settings.manage",
    label: "Settings",
    description: "Open this role access settings page.",
    section: "Settings",
  },
  {
    key: "audit_logs.view",
    label: "Audit Logs",
    description: "Review sensitive admin, finance, and permission changes.",
    section: "Settings",
  },
];

const DEFAULT_ROLE_PERMISSIONS: Record<RoleKey, PermissionKey[]> = {
  superadmin: DEFAULT_PERMISSION_DEFINITIONS.map((p) => p.key),
  admin: [
    "users.manage",
    "schedule.view",
    "schedule.create",
    "schedule.generate",
    "schedule.publish",
    "schedule.edit",
    "schedule.delete",
    "schedule.print",
    "schedule.manage_scores",
    "schedule.manage_staff",
    "schedule.edit_game_type",
    "seasons.manage",
    "divisions.manage",
    "teams.manage",
    "gyms.manage",
    "tournaments.manage",
    "tournaments.divisions.manage",
    "tournaments.teams.manage",
    "tournaments.schedule.manage",
    "tournaments.playoff.manage",
    "tournaments.gyms.manage",
    "payments.manage",
    "payroll.manage",
    "social.manage",
    "branding.manage",
  ],
  manager: [
    "schedule.view",
    "schedule.edit",
    "schedule.print",
    "schedule.manage_scores",
    "schedule.manage_staff",
    "schedule.edit_game_type",
  ],
  worker: ["schedule.view", "schedule.print", "schedule.manage_scores"],
  user: [],
};

function clonePermissions(rolePermissions: Record<RoleKey, PermissionKey[]>): Record<RoleKey, PermissionKey[]> {
  return DEFAULT_ROLES.reduce((acc, role) => {
    acc[role] = [...(rolePermissions[role] || [])];
    return acc;
  }, {} as Record<RoleKey, PermissionKey[]>);
}

function fallbackResponse(): RolePermissionsResponse {
  const rolePermissions = clonePermissions(DEFAULT_ROLE_PERMISSIONS);
  return {
    roles: [...DEFAULT_ROLES],
    permissions: [...DEFAULT_PERMISSION_DEFINITIONS],
    rolePermissions,
    userPermissions: [],
  };
}

@Injectable({ providedIn: "root" })
export class RolePermissionsService {
  private settings$?: Observable<RolePermissionsResponse>;
  private latest?: RolePermissionsResponse;
  private changesSubject = new ReplaySubject<RolePermissionsResponse>(1);

  constructor(private http: HttpClient) {}

  getRolePermissions(forceRefresh = false): Observable<RolePermissionsResponse> {
    if (!this.settings$ || forceRefresh) {
      this.settings$ = this.http
        .get<RolePermissionsResponse>(API_URL, { withCredentials: true })
        .pipe(
          map((response) => this.normalizeResponse(response)),
          tap((response) => this.store(response)),
          catchError(() => {
            const fallback = this.normalizeResponse(fallbackResponse());
            this.store(fallback);
            return of(fallback);
          }),
          shareReplay({ bufferSize: 1, refCount: true })
        );
    }
    return this.settings$;
  }

  watchRolePermissions(): Observable<RolePermissionsResponse> {
    this.getRolePermissions().subscribe();
    return this.changesSubject.asObservable();
  }

  saveRolePermissions(
    rolePermissions: Record<RoleKey, PermissionKey[]>
  ): Observable<RolePermissionsResponse> {
    return this.http
      .patch<RolePermissionsResponse>(
        API_URL,
        { rolePermissions },
        { withCredentials: true }
      )
      .pipe(
        map((response) => this.normalizeResponse(response)),
        tap((response) => {
          this.store(response);
          this.settings$ = of(response).pipe(shareReplay({ bufferSize: 1, refCount: true }));
        })
      );
  }

  resetCache(): void {
    this.settings$ = undefined;
    this.latest = undefined;
  }

  getDefaultRolePermissions(): Record<RoleKey, PermissionKey[]> {
    return clonePermissions(DEFAULT_ROLE_PERMISSIONS);
  }

  hasPermission(
    role: string | null | undefined,
    permission: PermissionKey,
    rolePermissions = this.latest?.rolePermissions
  ): boolean {
    if (role === "superadmin") return true;
    if (!role || !this.isRole(role)) return false;
    return !!rolePermissions?.[role]?.includes(permission);
  }

  private normalizeResponse(response: RolePermissionsResponse): RolePermissionsResponse {
    const permissions = response.permissions?.length ? response.permissions : [...DEFAULT_PERMISSION_DEFINITIONS];
    const rolePermissions = clonePermissions(response.rolePermissions || DEFAULT_ROLE_PERMISSIONS);
    rolePermissions.superadmin = permissions.map((p) => p.key);

    return {
      roles: response.roles?.length ? response.roles : [...DEFAULT_ROLES],
      permissions,
      rolePermissions,
      userPermissions: [...(response.userPermissions || [])],
      message: response.message,
    };
  }

  private store(response: RolePermissionsResponse): void {
    this.latest = response;
    this.changesSubject.next(response);
  }

  private isRole(role: string): role is RoleKey {
    return DEFAULT_ROLES.includes(role as RoleKey);
  }
}
